import { Link } from "gatsby"
import NavLink from "./navLink"
import RichText from "./richText"

const DEFAULT_IMAGE_CLASS = "w-full md:w-3/5 h-auto"

const ImageSection = ({ blok }) => {
	const {
		content,
		label,
		abstract,
		callToAction,
		flip,
		callToActionLink,
		backgroundClass,
		textColorClass,
		headingColorClass,
		buttonClassName,
		richTextWrapperClass,
		imagePosition,
		variant,
		imageSize,
	} = blok

	// Parse imagePosition to determine variant and position
	// Supports: "white-left", "white-right", "blue-left", "blue-right"
	// Also supports legacy: "left", "right" (uses variant prop or flip fallback)
	let parsedVariant = variant || null
	let position = null

	if (imagePosition) {
		const parts = imagePosition.split("-")
		if (parts.length >= 2) {
			// Format: "white-left", "blue-right", etc.
			const colorPart = parts[0].toLowerCase()
			const positionPart = parts[1].toLowerCase()
			
			// Validate color variant
			if (colorPart === "white" || colorPart === "blue") {
				parsedVariant = colorPart
			}
			
			// Validate position
			if (positionPart === "left" || positionPart === "right") {
				position = positionPart
			}
		} else if (parts.length === 1) {
			// Format: "left" or "right" only
			const positionPart = parts[0].toLowerCase()
			if (positionPart === "left" || positionPart === "right") {
				position = positionPart
			}
		}
	}

	// Determine image position (left or right)
	// Priority: imagePosition > flip prop > default (left)
	const isImageLeft = position
		? position === "left"
		: flip === false || flip === undefined

	// Determine color variant (white or blue)
	// Priority: parsedVariant from imagePosition/variant > flip prop > default (blue)
	const isWhiteVariant = parsedVariant 
		? parsedVariant === "white" 
		: flip === true

	// Set default CSS classes based on variant
	// White variant: white background, dark text
	// Blue variant: blue background (bg-neutral), white text
	const defaultBackgroundClass = isWhiteVariant ? "" : "bg-neutral"
	const defaultTextColorClass = isWhiteVariant ? "text-neutral" : "text-white"
	const defaultHeadingColorClass = isWhiteVariant
		? "text-neutral"
		: "text-white"
	const defaultButtonClassName = isWhiteVariant
		? "text-white rounded my-4 text-sm lg:text-md font-bold w-52 md:w-32 lg:w-40 xl:w-52 p-1 bg-primary hover:opacity-80"
		: "text-white rounded my-4 text-sm lg:text-md font-bold w-52 md:w-32 lg:w-40 xl:w-52 p-1 border-2 border-white hover:bg-primary hover:border-transparent hover:opacity-80"
	const defaultRichTextWrapperClass = isWhiteVariant
		? "font-light text-xs md:text-sm xl:text-lg text-center md:text-left"
		: "prose-white-text prose-invert text-white !text-white font-light text-xs md:text-sm xl:text-lg text-center md:text-left"

	// Use custom classes if provided, otherwise use defaults
	const containerBgClass = backgroundClass || defaultBackgroundClass
	const textColor = textColorClass || defaultTextColorClass
	const headingColor = headingColorClass || defaultHeadingColorClass
	const buttonClass = buttonClassName || defaultButtonClassName
	const richTextClass = richTextWrapperClass || defaultRichTextWrapperClass
	const resolvedImageClassName = imageSize || DEFAULT_IMAGE_CLASS

	// Render layout based on image position
	// Left: Image first, then content
	// Right: Content first, then image (image hidden on mobile, shown on desktop)
	if (isImageLeft) {
		return (
			<div
				className={`max-w-full flex flex-col md:flex-row justify-between ${containerBgClass}`}
			>
				<img
					className={resolvedImageClassName}
					src={content.filename}
					alt={content.alt}
				/>
				<div
					className={`${textColor} flex flex-grow flex-col place-self-center place-items-center md:items-start md:place-self-stretch md:justify-evenly md:align-center w-3/4 md:w-1/2 lg:w-1/4 mx-2 md:mx-10 my-2`}
				>
					<h2
						className={`${headingColor} text-xl lg:text-2xl xl:text-4xl font-bold my-2`}
					>
						{label}
					</h2>
					{abstract?.type ? (
						<div 
							className={richTextClass}
							style={!isWhiteVariant ? { color: 'white' } : {}}
						>
							<RichText 
								blok={blok} 
								content={abstract} 
								className={!isWhiteVariant ? "prose-invert !text-white" : ""}
							/>
						</div>
					) : (
						<div 
							className={richTextClass}
							style={!isWhiteVariant ? { color: 'white' } : {}}
						>
							{abstract}
						</div>
					)}
					{callToAction && (
						<NavLink
							link={callToActionLink}
							isSocialLink={false}
							className={buttonClass}
						>
							{callToAction}
						</NavLink>
					)}
				</div>
			</div>
		)
	} else {
		return (
			<div
				className={`max-w-full flex flex-col md:flex-row justify-between ${containerBgClass}`}
			>
				<img
					className={`${resolvedImageClassName} md:hidden`}
					src={content.filename}
					alt=""
				/>
				<div
					className={`${textColor} flex flex-grow flex-col place-self-center place-items-center md:items-start md:place-self-stretch md:justify-evenly md:align-center w-3/4 md:w-1/2 lg:w-1/4 mx-2 md:mx-10 my-2`}
				>
					<h2
						className={`${headingColor} text-xl lg:text-2xl xl:text-4xl font-bold my-2`}
					>
						{label}
					</h2>
					{abstract?.type ? (
						<div 
							className={richTextClass}
							style={!isWhiteVariant ? { color: 'white' } : {}}
						>
							<RichText 
								blok={blok} 
								content={abstract} 
								className={!isWhiteVariant ? "prose-invert !text-white" : ""}
							/>
						</div>
					) : (
						<div 
							className={richTextClass}
							style={!isWhiteVariant ? { color: 'white' } : {}}
						>
							{abstract}
						</div>
					)}
					{callToAction && (
						<NavLink
							link={callToActionLink}
							isSocialLink={false}
							className={buttonClass}
						>
							{callToAction}
						</NavLink>
					)}
				</div>
				<img
					className={`${resolvedImageClassName} hidden md:flex`}
					src={content.filename}
					alt={content.alt}
				/>
			</div>
		)
	}
}

export default ImageSection

//Top
/* <Link
className="text-white rounded my-4 text-sm lg:text-md font-bold w-52 md:w-32 lg:w-40  xl:w-52 p-1 bg-primary hover:opacity-80"
to={href}
>
{callToAction}
</Link> */

//Bottom

/* <Link
className="text-white rounded my-4 text-sm lg:text-md font-bold w-52 md:w-32 lg:w-40  xl:w-52 p-1 border-2 border-white hover:bg-primary hover:border-transparent hover:opacity-80"
to={href}
>
{callToAction}
</Link> */
